import { AnyAction } from 'redux';
import produce from 'immer';

import { ManagedProperty, PropertiesState } from '../types/state';
import { PROPERTIES_ACTIONS } from '../actions';
import { AUTH_ACTIONS } from '../actions/auth';
import { DropDownOption } from '../types';
import { ALL, ALLDropDownOption } from '../constants';

const initialState: PropertiesState = {
  propertyOptions: [],
  bedOptions: [],
  marketOptions: [],
};

const getBedroomOptions = (
  properties?: ManagedProperty[],
  selectedProperty?: string | number
) => {
  let bedOptions: DropDownOption[] = [];
  if (selectedProperty && properties) {
    if (selectedProperty === ALL || selectedProperty === 'portfolio') {
      let bedSet = new Set<string>();
      properties.forEach((property) =>
        property.BedRooms.forEach((bed) => bedSet.add(bed.toString()))
      );
      bedOptions = Array.from(bedSet)
        .sort()
        .map((elem) => ({ text: elem.toString(), value: elem.toString() }));
    } else {
      const [property] = properties.filter(
        (elem) => elem.PropertyCode === selectedProperty
      );
      bedOptions = property.BedRooms.map((elem) => ({
        text: elem.toString(),
        value: elem.toString(),
      }));
    }
  }

  return bedOptions.length > 0 ? [ALLDropDownOption].concat(bedOptions) : [];
};

const getOptions = (properties: ManagedProperty[]) => {
  const propertyOptions = properties.map((elem: ManagedProperty) => ({
    text: elem.PropertyName,
    value: elem.PropertyCode,
  }));
  const bedOptions = getBedroomOptions(properties, ALL);
  const marketSet = new Set<string>();

  properties
    .filter((elem) => elem.Market)
    .forEach((elem) => marketSet.add(elem.Market as string));

  const marketOptions = Array.from(marketSet).map((elem) => ({
    text: elem,
    value: elem,
  }));
  return {
    propertyOptions: [ALLDropDownOption].concat(propertyOptions),
    bedOptions,
    marketOptions: [ALLDropDownOption].concat(
      marketOptions as DropDownOption[]
    ),
  };
};

const propertyReducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case PROPERTIES_ACTIONS.SELECTED_MARKET: {
      if (!state.properties) return state;
      const options = getOptions(
        action.payload === ALL
          ? state.properties
          : state.properties?.filter((elem) => elem.Market === action.payload)
      );
      return produce(state, (draft) => {
        draft.selectedMarket = action.payload;
        draft.bedOptions = options.bedOptions;
        draft.propertyOptions = options.propertyOptions;
        draft.selectedBed =
          options.bedOptions.findIndex(
            (elem) => elem.value === state.selectedBed
          ) !== -1
            ? state.selectedBed
            : '' + options.bedOptions[0].value;
        draft.selectedProperty =
          options.propertyOptions.findIndex(
            (elem) => elem.value === state.selectedProperty?.id
          ) !== -1
            ? state.selectedProperty
            : {
                id: options.propertyOptions[0].value,
                name: options.propertyOptions[0].text,
              };
      });
    }
    case PROPERTIES_ACTIONS.SELECTED_PROPERTY: {
      const bedOptions = getBedroomOptions(
        state?.properties,
        action.payload.id
      );
      return produce(state, (draft) => {
        draft.selectedProperty = action.payload;
        draft.bedOptions = bedOptions;
        draft.selectedBed =
          bedOptions.findIndex((elem) => elem.value === state.selectedBed) !==
          -1
            ? state.selectedBed
            : '' + bedOptions[0].value;
      });
    }
    case PROPERTIES_ACTIONS.SELECTED_BEDROOM: {
      return produce(state, (draft) => {
        draft.selectedBed = action.payload;
      });
    }
    case PROPERTIES_ACTIONS.SET_PROPERTIES: {
      const { propertyOptions, bedOptions, marketOptions } = getOptions(
        action.payload
      );
      return produce(state, (draft) => {
        draft.properties = action.payload;
        draft.propertyOptions = propertyOptions;
        draft.bedOptions = bedOptions;
        draft.marketOptions = marketOptions as DropDownOption[];
        draft.selectedProperty = {
          id: propertyOptions[0].value,
          name: propertyOptions[0].text,
        };
        draft.selectedBed = '' + bedOptions[0].value;
        draft.selectedMarket = marketOptions[0].value.toString();
      });
    }
    case AUTH_ACTIONS.LOGOUT: {
      return initialState;
    }
    default:
      return state;
  }
};

export default propertyReducer;
